import { useEffect, useRef, useState } from "react";
import { ChevronDown, ShoppingCart, X } from "lucide-react";
import { Button } from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";
import { openBlankShopWindow, type CartLine } from "@/lib/shopCart";

const POLL_MS = 2000;
const MAX_WAIT_MS = 45 * 60 * 1000;

/**
 * „Aus Händlershop“ im Angebot: öffnet den IDS-Shop eines Händlers in einem neuen Fenster. Der Warenkorb kommt
 * beim Zurücksenden („An Handwerkersoftware senden“) in dieses Angebot und wird an die Positionen ANGEHÄNGT —
 * danach kann man einen weiteren Händler öffnen, die Positionen sammeln sich.
 */
export function ShopImportButton({
  onCart,
}: {
  onCart: (lines: CartLine[], supplierName: string) => void;
}) {
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);
  const shops = trpc.connector.shops.useQuery(undefined, { staleTime: 60_000 });
  const start = trpc.connector.startPunchout.useMutation();
  const consume = trpc.connector.consumeCart.useMutation();
  const [menu, setMenu] = useState(false);
  const [pending, setPending] = useState<{
    ref: string;
    name: string;
    since: number;
  } | null>(null);
  const onCartRef = useRef(onCart);
  onCartRef.current = onCart;
  const box = useRef<HTMLDivElement>(null);

  // Menü schließen bei Klick außerhalb
  useEffect(() => {
    if (!menu) return;
    const h = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node))
        setMenu(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menu]);

  // Auf den Warenkorb warten (Abfrage alle 2 s, höchstens 45 Minuten)
  useEffect(() => {
    if (!pending) return;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      if (Date.now() - pending.since > MAX_WAIT_MS) {
        setPending(null);
        return;
      }
      try {
        const p = await utils.connector.punchoutStatus.fetch(
          { sessionRef: pending.ref },
          { staleTime: 0 },
        );
        if (stopped) return;
        if (p?.status === "returned") {
          stopped = true;
          const r = await consume.mutateAsync({ sessionRef: pending.ref });
          setPending(null);
          if (r.lines.length) {
            onCartRef.current(r.lines as CartLine[], pending.name);
            addToast({
              type: "success",
              title: `${r.lines.length} Position(en) von ${pending.name} übernommen`,
              description: "Weiteren Händler öffnen, um mehr zu sammeln.",
            });
          } else {
            addToast({
              type: "info",
              title: "Warenkorb war leer oder bereits übernommen",
            });
          }
        }
      } catch {
        /* nächster Versuch */
      }
    };
    const id = setInterval(tick, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending?.ref]);

  const open = (shop: { id: string; name: string; ready: boolean }) => {
    setMenu(false);
    if (!shop.ready) {
      addToast({
        type: "warning",
        title: `${shop.name}: Zugang unvollständig`,
        description: "Unter Artikel → Lieferanten → Konnektor ergänzen.",
      });
      return;
    }
    const w = openBlankShopWindow();
    start.mutate(
      { supplierId: shop.id, returnTo: "quote" },
      {
        onSuccess: (r) => {
          if (w) w.location.href = r.redirectUrl;
          else window.open(r.redirectUrl, "_blank");
          setPending({
            ref: r.sessionRef,
            name: shop.name,
            since: Date.now(),
          });
        },
        onError: (e) => {
          w?.close();
          addToast({
            type: "error",
            title: "Shop konnte nicht geöffnet werden",
            description: e.message,
          });
        },
      },
    );
  };

  const list = shops.data ?? [];
  if (!list.length)
    return (
      <span className="text-xs text-text-tertiary">
        Kein Händlershop eingerichtet (Artikel → Lieferanten → IDS Connect)
      </span>
    );

  return (
    <div className="relative flex items-center gap-3 flex-wrap" ref={box}>
      <Button
        variant="secondary"
        loading={start.isPending}
        onClick={() => (list.length === 1 ? open(list[0]!) : setMenu((m) => !m))}
      >
        <ShoppingCart className="w-4 h-4" />
        {list.length === 1 ? `Aus ${list[0]!.name}` : "Aus Händlershop"}
        {list.length > 1 && <ChevronDown className="w-3.5 h-3.5" />}
      </Button>
      {menu && (
        <div className="absolute left-0 top-full mt-1 z-20 min-w-48 card p-1 shadow-lg">
          {list.map((s) => (
            <button
              key={s.id}
              className="w-full text-left px-3 py-1.5 text-sm rounded hover:bg-bg-hover disabled:text-text-tertiary"
              onClick={() => open(s)}
            >
              {s.name}
              {!s.ready && (
                <span className="text-2xs ml-2 text-warning">
                  Zugang fehlt
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      {pending && (
        <span className="text-xs text-text-secondary flex items-center gap-1.5">
          Warte auf Warenkorb von {pending.name} … im Shop „An
          Handwerkersoftware senden“ drücken.
          <button
            className="text-text-tertiary hover:text-danger"
            title="Nicht mehr warten"
            onClick={() => setPending(null)}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </span>
      )}
    </div>
  );
}
